import { useCallback, useEffect, useState } from 'react';
import {
  ProcessingSpinnerModal,
  UpgradeFailureModal,
  UpgradeSuccessModal,
} from '@ltvco/refresh-lib/payments';
import { Box, Grid, Link, Stack, Text } from '@ltvco/refresh-lib/theme';
import { useSession, updateSetting } from '@ltvco/refresh-lib/v1';
import { useContext } from 'react';
import { AppConfig, AppConstants } from '@ltvco/refresh-lib/ctx';
import {
  PaymentSection,
  OrderSummary,
  FAQ,
  SearchTypes,
  TrustBadges,
  TrialOffer,
  BenefitsSection,
  LegalFooter,
  Header,
} from './components/index';
import {
  PageContainer,
  PromoBar,
  PromoBarsContainer,
} from './components/Header/components';
import {
  AcceptBand,
  AcceptTitle,
  AcceptLogos,
} from './components/MainContent/PaymentSection/components';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  Bullet,
  BulletBackground,
  BulletNumber,
  StepHeader,
} from './components/MainContent/components';
import { useNavigate } from 'react-router-dom';
import {
  LegalFooterContent,
  LegalFooterLinks,
} from './components/Footer/components';
import {
  getFreemiumSubscribePageData,
  setFreemiumSubscribePageData,
} from '../../utils/freemiumSubscribePageStorage';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';

enum State {
  None,
  ProcessingPayment,
  Success,
  Error,
}

export const SubscribePage = () => {
  const { logError } = useContext(AppConfig);
  const [subscribeState, setSubscribeState] = useState(State.None);
  const [subscribeErrors, setSubscribeErrors] = useState<string[]>([]);
  const { useAccount, session } = useSession();
  const { refetch: refetchAccount } = useAccount(true);
  const { images } = useContext(AppConstants);
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  const freemiumSubscribePageData = getFreemiumSubscribePageData();
  const showBanner = freemiumSubscribePageData.show_banner;

  const extendedImages = images as any;
  const account = session.account;
  const isMemberPerksTestEnabled = useFeatureIsOn('BUMP-5186');
  const postSignupDestination = isMemberPerksTestEnabled
    ? '/member-perks'
    : '/dashboard';

  useEffect(() => {
    const hasActiveSubscription =
      account?.account.subscription_info?.subscription_state === 'Active' &&
      !account?.account.subscription_info.subscription_features
        ?.teaser_reports_only;

    if (hasActiveSubscription) {
      navigate('/dashboard');
    }
  }, [account, navigate]);

  const handleUpgradeSuccessAccept = useCallback(() => {
    setSubscribeState(State.None);
    navigate(postSignupDestination);
  }, [navigate, postSignupDestination]);

  const handleSubscriptionComplete = useCallback(() => {
    navigate(postSignupDestination);
  }, [navigate, postSignupDestination]);

  const handleUpgradeFailureTryAgain = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  const handleUpgradeFailureChangeCard = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  const handleUpgradeFailureClose = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  useEffect(() => {
    document.title = 'Subscribe - Bumper';
  }, []);

  const handleSkipSubscription = async () => {
    try {
      await updateSetting({
        setting: 'hide_freemium_upgrade_prompt',
        state: true,
      });
    } catch (error) {
      logError('Error updating hide_freemium_upgrade_prompt', error as Error);
    } finally {
      setFreemiumSubscribePageData({ skip_page: true });
      navigate(postSignupDestination);
    }
  };

  return (
    <Box sx={{ backgroundColor: 'common.white', minHeight: '100vh' }}>
      <PromoBarsContainer>
        {showBanner && (
          <PromoBar className="skip-banner">
            Not ready to commit to Bumper Pro?{' '}
            <Link
              component="button"
              type="button"
              onClick={handleSkipSubscription}
            >
              Skip to Bumper Garage
            </Link>
          </PromoBar>
        )}
        <PromoBar>
          {isMobile ? 'Special Offer!' : 'Take advantage of our special offer!'}
        </PromoBar>
      </PromoBarsContainer>
      <PageContainer>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Header />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Text
              variant="h1"
              textAlign="center"
              fontWeight={400}
              fontStyle="italic"
              sx={{ mt: 2 }}
              fontSize={{ xs: 20, md: 40 }}
            >
              Run reports for just $1*
            </Text>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>1</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Special $1 Trial Offer
                  </Text>
                </StepHeader>
                <TrialOffer isSelected={true} onSelect={() => {}} />
                <Box sx={{ mt: 1.5 }}>
                  <AcceptBand>
                    <AcceptTitle>WE ACCEPT</AcceptTitle>
                    <AcceptLogos>
                      <img
                        src={extendedImages.subscribePage.creditCardsLogo}
                        alt="Cards"
                        height={28}
                      />
                    </AcceptLogos>
                  </AcceptBand>
                </Box>

                {/* Mobile: Show sidebar components after AcceptBand */}
                {isMobile && (
                  <Box sx={{ mt: 2 }}>
                    <Stack spacing={2}>
                      <FAQ />
                    </Stack>
                  </Box>
                )}
              </Box>

              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>2</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Your membership benefits include
                  </Text>
                </StepHeader>
                <BenefitsSection />
              </Box>

              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>3</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Final step - just enter your details
                  </Text>
                </StepHeader>
                {isMobile && <OrderSummary />}
                <PaymentSection
                  onSubscriptionComplete={handleSubscriptionComplete}
                />
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }} mt={{ md: 5.5, xs: 0 }}>
            <Stack spacing={2}>
              {!isMobile && <OrderSummary />}
              {!isMobile && <FAQ />}
              {!isMobile && <SearchTypes />}
              <TrustBadges />
            </Stack>
          </Grid>
        </Grid>
        <LegalFooter handleSkipSubscription={handleSkipSubscription} />
      </PageContainer>
      <Stack width="100%" alignItems="center" justifyContent="center">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="center"
          spacing={2}
          justifyContent="space-between"
          width="80%"
          mt={2}
          textAlign="left"
        >
          <LegalFooterContent variant="body2">
            © {new Date().getFullYear()} Bumper.com. All rights reserved.
            Bumper®, and the BUMPER logo are trademarks of Bumper LLC.{' '}
          </LegalFooterContent>
          <Box>
            <LegalFooterLinks
              href="https://www.bumper.com/terms/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </LegalFooterLinks>{' '}
            |{' '}
            <LegalFooterLinks
              href="https://www.bumper.com/privacy-policy/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </LegalFooterLinks>{' '}
            |{' '}
            <LegalFooterLinks
              href="https://www.bumper.com/nmvtis-disclaimer/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              NMVTIS Disclaimer
            </LegalFooterLinks>
          </Box>
        </Stack>
      </Stack>
      <ProcessingSpinnerModal
        open={subscribeState === State.ProcessingPayment}
      />
      <UpgradeSuccessModal
        open={subscribeState === State.Success}
        onAccept={handleUpgradeSuccessAccept}
      />
      <UpgradeFailureModal
        open={subscribeState === State.Error}
        errors={subscribeErrors}
        onTryAgain={handleUpgradeFailureTryAgain}
        onChangeCard={handleUpgradeFailureChangeCard}
        onClose={handleUpgradeFailureClose}
      />
    </Box>
  );
};
